import { useTranslation } from '@shared/core/i18n';
import { useMemo } from 'react';
import { SearchParams } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { formatScheduleItemDate, formatScheduleTimeframe } from '@shared/utils/scheduleFormatting';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { getTimeZoneAbbr } from './ScheduleList.utils';

type RouteBuilder = Readonly<{
  buildRelativeUrl: (eventHandle: string, searchParams: SearchParams) => string;
}>;

export type ScheduleListTranslations = Readonly<{
  addToCalendar: string;
  whereToStay: string;
  addToCal: string;
  dressCode: string;
  getDirections: string;
  moreDetails: string;
  timeframe: (startDateTime?: number, endDateTime?: number) => string | undefined;
  timeframeTz: (startDateTime?: number, endDateTime?: number, timezone?: string) => string | undefined;
  timeTz: (time?: string, timezoneAbbr?: string) => string | undefined;
  description: (
    bookYourStay: RouteBuilder,
    description?: string | null,
    dressCode?: string | null,
    eventHandle?: string | null,
    clientUri?: string | null,
    includeAccommodationsLink?: boolean,
  ) => string | undefined;
  toBeAnnounced: string;
  toBeAnnouncedDescription: string;
}>;

export const useTranslations = (): ScheduleListTranslations => {
  const { t, tq } = useTranslation('guestKit');
  const { language } = useLocaleContext();
  const translations = t('common');

  const value = useMemo<ScheduleListTranslations>(() => {
    return {
      addToCalendar: translations.addToCalendar(),
      whereToStay: translations.whereToStay(),
      addToCal: translations.addToCal(),
      dressCode: translations.dressCode(),
      getDirections: translations.getDirections(),
      moreDetails: translations.moreDetails(),
      timeframe: (startDateTime?: number, endDateTime?: number): string | undefined => {
        const start = startDateTime ? formatScheduleItemDate(new Date(startDateTime), language) : undefined;
        if (!start) return undefined;

        const end = endDateTime ? formatScheduleItemDate(new Date(endDateTime), language) : undefined;

        // Single time only - return just the time
        if (!end) return start.time;

        // Use shared formatting logic
        return formatScheduleTimeframe({
          start,
          end,
          timeframeMultiDay: translations.timeframeMultiDay,
          timeframe: translations.timeframe,
          language,
        });
      },
      description: (bookYourStay, description, dressCode, eventHandle, clientUri, includeAccommodationsLink = true) => {
        const contentParts: string[] = [];
        if (description) contentParts.push(description);
        if (dressCode) contentParts.push(`${translations.dressCode()}: ${dressCode}`);

        if (!eventHandle || !clientUri) {
          return contentParts.length > 0 ? tq`${contentParts.join('\n\n')}` : undefined;
        }

        const normalizedClientUri = clientUri.endsWith('/') ? clientUri.slice(0, -1) : clientUri;
        const scheduleUrl = new URL(`${normalizedClientUri}/${eventHandle}/schedule`);
        scheduleUrl.searchParams.set('utm_medium', 'calendar');
        scheduleUrl.searchParams.set('utm_campaign', 'schedule_item');
        scheduleUrl.searchParams.set('utm_source', 'schedule_list_item');

        const linkParts: string[] = [`${translations.moreDetails()}:\n${scheduleUrl.toString()}`];

        if (includeAccommodationsLink) {
          const accommodationsUrl =
            normalizedClientUri +
            bookYourStay.buildRelativeUrl(eventHandle, {
              utm_medium: 'calendar',
              utm_campaign: 'schedule_item',
              utm_source: 'schedule_list_item',
            });
          linkParts.push(`${translations.whereToStay()}:\n${accommodationsUrl}`);
        }

        const allParts = [...contentParts, linkParts.join('\n\n')];
        return tq`${allParts.join('\n\n')}`;
      },
      timeframeTz: (startDateTime?: number, endDateTime?: number, timezone?: string): string | undefined => {
        const start = startDateTime ? formatScheduleItemDate(new Date(startDateTime), language, timezone) : undefined;

        if (!start) return undefined;

        const end = endDateTime ? formatScheduleItemDate(new Date(endDateTime), language, timezone) : undefined;

        const timezoneAbbr = getTimeZoneAbbr(startDateTime, timezone);

        // Single time only - return time with timezone
        if (!end) return translations.timeTz({ time: start.time, timezoneAbbr });

        // Use shared formatting logic
        return formatScheduleTimeframe({
          start,
          end,
          timeframeMultiDay: translations.timeframeMultiDayTz,
          timeframe: translations.timeframeTz,
          language,
          timezoneAbbr,
        });
      },
      timeTz: (time, timezoneAbbr) => translations.timeTz({ time, timezoneAbbr }),
      toBeAnnounced: translations.toBeAnnouncedHeader(),
      toBeAnnouncedDescription: translations.toBeAnnouncedDescription(),
    };
  }, [language, tq, translations]);

  return value;
};
